/**
 * Super-admin reads for the tenant details page. The tenant-scoped hooks (useTenant, useFunds, useUsers, ...) go
 * through /resource, which only returns the caller's own tenant, so these read any tenant through /admin/tenants.
 */
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@diggens/api-client';
import type { Fund, MetricDefinition, PortfolioCompany, Submission, Tenant, User } from '@diggens/types';

export type AdminTenantUser = Pick<User, 'id' | 'full_name' | 'email' | 'role' | 'status' | 'portfolio_company_id' | 'created_at'>;
export type AdminTenantFund = Pick<Fund, 'id' | 'name' | 'reporting_frequency' | 'reporting_day_of_month' | 'created_at' | 'updated_at'>;
export type AdminTenantCompany = Pick<PortfolioCompany, 'id' | 'name' | 'sector'>;
export type AdminTenantMetricDefinition = Pick<MetricDefinition, 'id' | 'name' | 'data_type' | 'is_required' | 'display_order'>;
export type AdminTenantSubmission = Pick<Submission, 'id' | 'period' | 'status' | 'source_type' | 'submitted_at'>;

export interface AdminTenantDetails {
  account_status: string;
  trial_ends_at: string | null;
  subscription_status: string | null;
  subscription_owner: string | null;
  current_period_end: string | null;
  funds: number;
  portfolio_companies: number;
  submissions_30d: number;
  metrics: number;
  total_users: number;
  admin_users: number;
  portco_users: number;
  pending_users: number;
}

const tenantPath = (tenantId: string) => `/admin/tenants/${encodeURIComponent(tenantId)}`;

/** Every tenant on the platform; useTenants can only list the caller's own. */
export function useAdminTenants() {
  return useQuery({
    queryKey: ['admin-tenants'],
    queryFn: () => apiFetch<Tenant[]>('/admin/tenants'),
  });
}

export function useAdminTenant(tenantId: string) {
  return useQuery({
    queryKey: ['admin-tenant', tenantId],
    queryFn: () => apiFetch<Tenant>(tenantPath(tenantId)),
    enabled: !!tenantId,
  });
}

/** Counts of funds, companies, metrics and users. */
export function useAdminTenantDetails(tenantId: string) {
  return useQuery({
    queryKey: ['tenant-details', tenantId],
    queryFn: () => apiFetch<AdminTenantDetails>(`/admin/tenant-details/${encodeURIComponent(tenantId)}`),
    enabled: !!tenantId,
  });
}

export function useAdminTenantUsers(tenantId: string) {
  return useQuery({
    queryKey: ['admin-tenant', tenantId, 'users'],
    queryFn: () => apiFetch<AdminTenantUser[]>(`${tenantPath(tenantId)}/users`),
    enabled: !!tenantId,
  });
}

export function useAdminCompanyUsers(tenantId: string, companyId: string) {
  return useQuery({
    queryKey: ['admin-tenant', tenantId, 'company', companyId, 'users'],
    queryFn: () => apiFetch<AdminTenantUser[]>(`${tenantPath(tenantId)}/users?portfolio_company_id=${encodeURIComponent(companyId)}`),
    enabled: !!tenantId && !!companyId,
  });
}

export function useAdminTenantFunds(tenantId: string) {
  return useQuery({
    queryKey: ['admin-tenant', tenantId, 'funds'],
    queryFn: () => apiFetch<AdminTenantFund[]>(`${tenantPath(tenantId)}/funds`),
    enabled: !!tenantId,
  });
}

export function useAdminFundCompanies(tenantId: string, fundId: string) {
  return useQuery({
    queryKey: ['admin-tenant', tenantId, 'fund', fundId, 'companies'],
    queryFn: () => apiFetch<AdminTenantCompany[]>(`${tenantPath(tenantId)}/funds/${encodeURIComponent(fundId)}/portfolio-companies`),
    enabled: !!tenantId && !!fundId,
  });
}

export function useAdminFundMetricDefinitions(tenantId: string, fundId: string) {
  return useQuery({
    queryKey: ['admin-tenant', tenantId, 'fund', fundId, 'metric-definitions'],
    queryFn: () => apiFetch<AdminTenantMetricDefinition[]>(`${tenantPath(tenantId)}/funds/${encodeURIComponent(fundId)}/metric-definitions`),
    enabled: !!tenantId && !!fundId,
  });
}

export function useAdminCompanySubmissions(tenantId: string, companyId: string) {
  return useQuery({
    queryKey: ['admin-tenant', tenantId, 'company', companyId, 'submissions'],
    queryFn: () => apiFetch<AdminTenantSubmission[]>(`${tenantPath(tenantId)}/portfolio-companies/${encodeURIComponent(companyId)}/submissions`),
    enabled: !!tenantId && !!companyId,
  });
}
